
import React, { useState, useEffect, useRef } from "react";
import { Download, Copy, Check, FileText, BarChart3, History, AlertTriangle, Save, Plus, Trash, X, Undo, Redo, RotateCcw } from "lucide-react";
import { Button } from "./ui/Button";
import { renderPdf } from "../utils/api";
import { FeedbackModal } from "./FeedbackModal";

interface ResultsViewProps {
  data: any; // { resume_data, cover_letter_text, resume_pdf_url, cover_letter_pdf_url, scores, diff, original_text }
}

const API_BASE = "http://localhost:8000"; // For full URL links

const AutoResizeTextarea = ({ value, onChange, className, ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => {
  const ref = React.useRef<HTMLTextAreaElement>(null);

  const adjustHeight = () => {
    if (ref.current) {
      ref.current.style.height = "auto";
      ref.current.style.height = ref.current.scrollHeight + "px";
    }
  };

  React.useEffect(() => {
    adjustHeight();
  }, [value]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      className={className}
      style={{ resize: "none", overflow: "hidden" }}
      {...props}
    />
  );
};


export function ResultsView({ data }: ResultsViewProps) {
  const [activeTab, setActiveTab] = useState<"resume" | "cover_letter" | "job_description" | "diff">("resume");
  const [copied, setCopied] = useState<string | null>(null);
  
  // Local state for inline editing
  const [resumeData, setResumeData] = useState(data.resume_data);
  const [coverLetterText, setCoverLetterText] = useState(data.cover_letter_text);
  const [jobDescription] = useState(data.job_description || "");
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  
  // Feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const hasDownloadedRef = useRef(false);
  
  // Track which sections are visible
  const [visibleSections, setVisibleSections] = useState({
    summary: true,
    skills: true,
    experience: true,
    education: true
  });

  // History management for undo/redo
  const [history, setHistory] = useState<Array<{resumeData: any, visibleSections: any}>>([{
    resumeData: data.resume_data,
    visibleSections: { summary: true, skills: true, experience: true, education: true }
  }]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const isUndoRedoAction = useRef(false);
  
  // Store original data for reset functionality
  const originalData = useRef({
    resumeData: data.resume_data,
    coverLetterText: data.cover_letter_text,
    visibleSections: { summary: true, skills: true, experience: true, education: true }
  });

  useEffect(() => {
    setResumeData(data.resume_data);
    setCoverLetterText(data.cover_letter_text);
    // Reset history when new data comes in
    setHistory([{
      resumeData: data.resume_data,
      visibleSections: { summary: true, skills: true, experience: true, education: true }
    }]);
    setHistoryIndex(0);
    // Update original data reference
    originalData.current = {
      resumeData: data.resume_data,
      coverLetterText: data.cover_letter_text,
      visibleSections: { summary: true, skills: true, experience: true, education: true }
    };
  }, [data]);

  // Save state to history whenever resumeData or visibleSections change
  useEffect(() => {
    // Skip if this is an undo/redo/reset action
    if (isUndoRedoAction.current) {
      return; // Don't reset the flag here, let the action handler do it
    }
    
    const newState = { resumeData, visibleSections };
    const currentState = history[historyIndex];
    
    // Only add to history if state actually changed
    if (JSON.stringify(currentState) !== JSON.stringify(newState)) {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(newState);
      // Limit history to 50 states to prevent memory issues
      if (newHistory.length > 50) {
        newHistory.shift();
      } else {
        setHistoryIndex(historyIndex + 1);
      }
      setHistory(newHistory);
    }
  }, [resumeData, visibleSections]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [historyIndex, history]);

  const handleCopy = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };
  
  // Helper to check if we should show feedback modal
  const checkFeedbackEligibility = () => {
      // 1. Check if user already submitted feedback recently? (Maybe in last 30 days)
      const lastSubmitted = localStorage.getItem('ra_feedback_submitted'); // Timestamp
      if (lastSubmitted) {
          const daysSinceSubmission = (Date.now() - parseInt(lastSubmitted)) / (1000 * 60 * 60 * 24);
          if (daysSinceSubmission < 30) return false; // Don't ask for a month if they submitted
      }
      
      // 2. Check if we showed the prompt recently (e.g. 3 days)
      const lastShown = localStorage.getItem('ra_feedback_last_shown');
      if (lastShown) {
          const daysSinceShown = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
          if (daysSinceShown < 3) return false; // Don't ask more than once every 3 days
      }
      
      return true;
  };

  const downloadBlob = (blob: Blob, filename: string) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
  };

  const generateAndDownload = async (type: "resume" | "cover_letter") => {
      const currentData = {
          resume: resumeData,
          cover_letter: coverLetterText
      };
      const blob = await renderPdf(currentData, type);
      const filename = `${resumeData.name}_${type === 'resume' ? 'Resume' : 'Cover_Letter'}.pdf`;
      downloadBlob(blob, filename);
  };

  const handleDownloadPdf = async (type: "resume" | "cover_letter") => {
      try {
          setIsPdfGenerating(true);
          await generateAndDownload(type);
          
          // Show feedback modal logic (Occasional)
          if (!hasDownloadedRef.current && !feedbackSubmitted) {
              hasDownloadedRef.current = true;
              
              const shouldShow = checkFeedbackEligibility();
              if (shouldShow) {
                  // Small delay to let download start
                  setTimeout(() => {
                      setShowFeedbackModal(true);
                       // Update last shown timestamp
                      localStorage.setItem('ra_feedback_last_shown', Date.now().toString());
                  }, 500);
              }
          }
      } catch (e) {
          console.error("PDF generation failed", e);
          alert("Failed to generate PDF");
      } finally {
          setIsPdfGenerating(false);
      }
  };

  const handleDownloadBoth = async () => {
      try {
          setIsPdfGenerating(true);
          // Generate both in parallel or sequence
          await generateAndDownload("resume");
          // Small delay to ensure browser handles first download nicely
          await new Promise(resolve => setTimeout(resolve, 500)); 
          await generateAndDownload("cover_letter");
          
          // Show feedback modal logic (Occasional)
          if (!hasDownloadedRef.current && !feedbackSubmitted) {
              hasDownloadedRef.current = true;
              
              const shouldShow = checkFeedbackEligibility();
              if (shouldShow) {
                  // Small delay to let downloads start
                  setTimeout(() => {
                      setShowFeedbackModal(true);
                      localStorage.setItem('ra_feedback_last_shown', Date.now().toString());
                  }, 1000);
              }
          }
      } catch (e) {
          console.error("PDF generation failed", e);
          alert("Failed to generate PDFs");
      } finally {
          setIsPdfGenerating(false);
      }
  };

  // Undo/Redo functions
  const handleUndo = () => {
    if (historyIndex > 0) {
      isUndoRedoAction.current = true;
      const newIndex = historyIndex - 1;
      const state = history[newIndex];
      setResumeData(state.resumeData);
      setVisibleSections(state.visibleSections);
      setHistoryIndex(newIndex);
      
      // Reset flag after effects run
      setTimeout(() => {
        isUndoRedoAction.current = false;
      }, 100);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      isUndoRedoAction.current = true;
      const newIndex = historyIndex + 1;
      const state = history[newIndex];
      setResumeData(state.resumeData);
      setVisibleSections(state.visibleSections);
      setHistoryIndex(newIndex);
      
      // Reset flag after effects run
      setTimeout(() => {
        isUndoRedoAction.current = false;
      }, 100);
    }
  };

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // Reset to original generated resume
  const handleReset = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (window.confirm('Are you sure you want to reset to the original generated resume? All your edits will be lost.')) {
      // Simply reload the original data and clear the undo/redo stack
      const originalResume = JSON.parse(JSON.stringify(originalData.current.resumeData));
      const originalCover = originalData.current.coverLetterText;
      const originalSections = { ...originalData.current.visibleSections };
      
      // Clear the undo/redo stack first
      setHistory([{
        resumeData: originalResume,
        visibleSections: originalSections
      }]);
      setHistoryIndex(0);
      
      // Then set the data (this will add to the fresh history stack)
      setResumeData(originalResume);
      setCoverLetterText(originalCover);
      setVisibleSections(originalSections);
    }
  };

  // Helper to update resume fields
  const updateResume = (field: string, value: any) => {
      setResumeData((prev: any) => ({ ...prev, [field]: value }));
  };
  
  const updateExperience = (index: number, field: string, value: any) => {
      const newExp = [...resumeData.experience];
      newExp[index] = { ...newExp[index], [field]: value };
      setResumeData((prev: any) => ({ ...prev, experience: newExp }));
  };

  const updateBullet = (expIndex: number, bulletIndex: number, value: string) => {
      const newExp = [...resumeData.experience];
      const newBullets = [...newExp[expIndex].bullets];
      newBullets[bulletIndex] = value;
      newExp[expIndex].bullets = newBullets;
      setResumeData((prev: any) => ({ ...prev, experience: newExp }));
  };
  
  const updateSkills = (value: string) => {
      // Store the raw text value, don't split immediately
      // This allows users to type commas without them being removed
      updateResume("skills", value);
  };

  const addBullet = (expIndex: number) => {
      const newExp = [...resumeData.experience];
      newExp[expIndex].bullets.push("Add bullet point");
      setResumeData((prev: any) => ({ ...prev, experience: newExp }));
  };

  const removeBullet = (expIndex: number, bulletIndex: number) => {
      const newExp = [...resumeData.experience];
      newExp[expIndex].bullets.splice(bulletIndex, 1);
      setResumeData((prev: any) => ({ ...prev, experience: newExp }));
  };

  // Section management functions
  const removeSection = (section: 'summary' | 'skills' | 'experience' | 'education') => {
      setVisibleSections(prev => ({ ...prev, [section]: false }));
  };

  const addExperience = () => {
      const newExp = [{
          title: "Job Title",
          company: "Company Name",
          duration: "Month Year - Month Year",
          bullets: ["Add your key achievements and responsibilities"]
      }, ...resumeData.experience];
      setResumeData((prev: any) => ({ ...prev, experience: newExp }));
  };

  const removeExperience = (index: number) => {
      const newExp = [...resumeData.experience];
      newExp.splice(index, 1);
      setResumeData((prev: any) => ({ ...prev, experience: newExp }));
  };

  const addEducation = () => {
      const newEdu = [...resumeData.education, {
          institution: "University Name",
          degree: "Degree Name",
          year: "Year"
      }];
      setResumeData((prev: any) => ({ ...prev, education: newEdu }));
  };

  const removeEducation = (index: number) => {
      const newEdu = [...resumeData.education];
      newEdu.splice(index, 1);
      setResumeData((prev: any) => ({ ...prev, education: newEdu }));
  };

  const updateEducation = (index: number, field: string, value: any) => {
      const newEdu = [...resumeData.education];
      newEdu[index] = { ...newEdu[index], [field]: value };
      setResumeData((prev: any) => ({ ...prev, education: newEdu }));
  };

  const addSummaryParagraph = () => {
      updateResume("summary", "Add your professional summary highlighting your key skills and experience.");
  };

  const addSkill = () => {
      // Handle both string and array formats
      const currentSkills = typeof resumeData.skills === 'string' ? resumeData.skills : resumeData.skills.join(", ");
      const newSkills = currentSkills ? currentSkills + ", New Skill" : "New Skill";
      updateResume("skills", newSkills);
  };

  return (
    <div className="flex flex-col h-full bg-slate-50">
      {data.scores && (
          <div className="bg-slate-900 text-white p-4 rounded-t-xl shadow-md z-10">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                  <BarChart3 className="h-5 w-5 text-blue-400" />
                  <div>
                      <h2 className="text-sm font-medium text-slate-300">ATS Compatibility Score</h2>
                      <div className="text-2xl font-bold flex items-baseline gap-2">
                          {data.scores.after?.total_score}
                          <span className="text-sm font-normal text-slate-400 line-through decoration-slate-500">
                             {data.scores.before?.total_score}
                          </span>
                          <span className="text-xs font-medium text-green-400 bg-green-400/10 px-1.5 py-0.5 rounded">
                             +{data.scores.after?.total_score - data.scores.before?.total_score}
                          </span>
                      </div>
                  </div>
              </div>
              
              {/* Missing Keywords Section */}
              {data.scores.after?.breakdown?.missing_keywords && data.scores.after?.breakdown?.missing_keywords.length > 0 && (
                  <div className="bg-slate-800/50 rounded-lg p-3 max-w-lg">
                      <div className="flex items-center gap-1.5 text-amber-400 mb-1.5">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          <span className="text-xs font-bold uppercase tracking-wide">Missing Keywords</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                          {data.scores.after.breakdown.missing_keywords.map((k: string, i: number) => (
                              <span key={i} className="text-[10px] bg-slate-700 text-slate-200 px-1.5 py-0.5 rounded border border-slate-600">
                                  {k}
                              </span>
                          ))}
                      </div>
                  </div>
              )}
            </div>
      </div>
      )}

      <div className="flex items-center gap-4 mb-2 border-b border-slate-200 mt-4 px-4">
        <button
           onClick={() => setActiveTab("resume")}
           className={`pb-3 px-1 text-sm font-medium transition-colors ${activeTab === "resume" ? "border-b-2 border-slate-900 text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
        >
           Optimized Resume
        </button>
        <button
           onClick={() => setActiveTab("cover_letter")}
           className={`pb-3 px-1 text-sm font-medium transition-colors ${activeTab === "cover_letter" ? "border-b-2 border-slate-900 text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
        >
           Cover Letter
        </button>
        <button
           onClick={() => setActiveTab("job_description")}
           className={`pb-3 px-1 text-sm font-medium transition-colors ${activeTab === "job_description" ? "border-b-2 border-slate-900 text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
        >
           Job Description
        </button>
        {/*<button
           onClick={() => setActiveTab("diff")}
           className={`pb-3 px-1 text-sm font-medium transition-colors ${activeTab === "diff" ? "border-b-2 border-slate-900 text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
        >
           Changes & Analysis
        </button> */}
      </div>

      <div className="flex-1 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden flex flex-col mx-4 mb-4">
          {/* Toolbar */}
          <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50">
             <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700 capitalize">
                    {activeTab.replace("_", " ")} Preview
                    {activeTab !== "job_description" && <span className="ml-2 text-xs font-normal text-slate-400 bg-slate-200 px-1.5 py-0.5 rounded">Editable</span>}
                </span>
             </div>
             <div className="flex items-center gap-2">
                 {activeTab === "resume" && (
                     <div className="flex items-center gap-1 mr-2 border-r pr-2">
                         <Button
                            variant="outline"
                            size="sm"
                            onClick={handleUndo}
                            disabled={!canUndo}
                            title="Undo (Cmd+Z)"
                         >
                            <Undo className="h-4 w-4" />
                         </Button>
                         <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRedo}
                            disabled={!canRedo}
                            title="Redo (Cmd+Shift+Z)"
                         >
                            <Redo className="h-4 w-4" />
                         </Button>
                         <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => handleReset(e)}
                            title="Reset to original"
                            className="ml-1"
                         >
                            <RotateCcw className="h-4 w-4" />
                         </Button>
                     </div>
                 )}
                 {activeTab !== "diff" && (
                     <div className="flex items-center gap-2">
                         <Button size="sm" variant="outline" onClick={handleDownloadBoth} disabled={isPdfGenerating}>
                             {isPdfGenerating ? (
                                  <span className="flex items-center"><Save className="h-4 w-4 mr-2 animate-spin" /> Generating...</span>
                             ) : (
                                  <span className="flex items-center"><Download className="h-4 w-4 mr-2" /> Download Resume + Cover letter</span>
                             )}
                         </Button>
                         <Button size="sm" onClick={() => handleDownloadPdf(activeTab as any)} disabled={isPdfGenerating}>
                            {isPdfGenerating ? (
                                 <span className="flex items-center"><Save className="h-4 w-4 mr-2 animate-spin" /> Generating...</span>
                            ) : (
                                 <span className="flex items-center"><Download className="h-4 w-4 mr-2" /> Download {activeTab.split("_").join(" ")}</span>
                            )}
                         </Button>
                     </div>
                 )}
             </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-8 bg-white text-slate-800">
              {activeTab === "resume" ? (
                  <div className="max-w-3xl mx-auto space-y-6">
                      <div className="text-center space-y-2">
                          <input 
                              type="text" 
                              value={resumeData.name} 
                              onChange={(e) => updateResume("name", e.target.value)}
                              className="text-2xl font-bold uppercase tracking-wide text-center w-full border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none transition-colors bg-transparent"
                          />
                          <input 
                              type="text" 
                              value={resumeData.contact_info} 
                              onChange={(e) => updateResume("contact_info", e.target.value)}
                              className="text-sm text-slate-600 text-center w-full border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none transition-colors bg-transparent"
                          />
                      </div>

                      {visibleSections.summary && (
                      <section className="group/section relative">
                          <div className="flex items-center justify-between mb-3 border-b pb-1">
                              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">Professional Summary</h2>
                              <button 
                                  onClick={() => removeSection('summary')}
                                  className="opacity-0 group-hover/section:opacity-100 transition-opacity text-red-500 hover:text-red-700 p-1"
                                  title="Remove section"
                              >
                                  <X size={16} />
                              </button>
                          </div>
                          {resumeData.summary && resumeData.summary.trim().length > 0 ? (
                              <AutoResizeTextarea 
                                  value={resumeData.summary} 
                                  onChange={(e) => updateResume("summary", e.target.value)}
                                  className="w-full text-sm leading-relaxed p-2 -ml-2 rounded border border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none bg-transparent"
                              />
                          ) : (
                              <button 
                                  onClick={addSummaryParagraph}
                                  className="mt-2 border border-dashed border-slate-300 p-3 rounded-md text-sm text-slate-500 hover:text-blue-600 hover:border-blue-400 flex items-center gap-2 w-full justify-center transition-colors"
                              >
                                  <Plus size={16} /> Add Professional Summary
                              </button>
                          )}
                      </section>
                      )}

                      {visibleSections.skills && (
                      <section className="group/section relative">
                          <div className="flex items-center justify-between mb-3 border-b pb-1">
                              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">Skills</h2>
                              <button 
                                  onClick={() => removeSection('skills')}
                                  className="opacity-0 group-hover/section:opacity-100 transition-opacity text-red-500 hover:text-red-700 p-1"
                                  title="Remove section"
                              >
                                  <X size={16} />
                              </button>
                          </div>
                          {resumeData.skills && (typeof resumeData.skills === 'string' ? resumeData.skills.length > 0 : resumeData.skills.length > 0) ? (
                              <>
                                  <AutoResizeTextarea 
                                      value={typeof resumeData.skills === 'string' ? resumeData.skills : resumeData.skills.join(", ")} 
                                      onChange={(e) => updateSkills(e.target.value)}
                                      className="w-full text-sm p-2 -ml-2 rounded border border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none bg-transparent"
                                  />
                                  <p className="text-xs text-slate-400 mt-1">Comma separated</p>
                              </>
                          ) : (
                              <button 
                                  onClick={addSkill}
                                  className="mt-2 border border-dashed border-slate-300 p-3 rounded-md text-sm text-slate-500 hover:text-blue-600 hover:border-blue-400 flex items-center gap-2 w-full justify-center transition-colors"
                              >
                                  <Plus size={16} /> Add Skills
                              </button>
                          )}
                      </section>
                      )}

                       {visibleSections.experience && (
                       <section className="group/section relative">
                          <div className="flex items-center justify-between mb-3 border-b pb-1">
                              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">Experience</h2>
                              <button 
                                  onClick={() => removeSection('experience')}
                                  className="opacity-0 group-hover/section:opacity-100 transition-opacity text-red-500 hover:text-red-700 p-1"
                                  title="Remove section"
                              >
                                  <X size={16} />
                              </button>
                          </div>
                          {resumeData.experience && resumeData.experience.length > 0 ? (
                          <>
                          <button 
                              onClick={addExperience}
                              className="mb-4 border border-dashed border-slate-300 p-3 rounded-md text-sm text-slate-500 hover:text-blue-600 hover:border-blue-400 flex items-center gap-2 w-full justify-center transition-colors"
                          >
                              <Plus size={16} /> Add Experience
                          </button>
                          <div className="space-y-8">
                             {resumeData.experience.map((exp: any, i: number) => (
                                 <div key={i} className="group/entry relative border border-transparent hover:border-slate-200 rounded-lg p-2 -m-2 transition-colors">
                                     <div className="flex justify-between items-start mb-1 gap-4">
                                         <div className="flex-1 flex justify-between items-baseline gap-4">
                                             <input 
                                                  value={exp.title}
                                                  onChange={(e) => updateExperience(i, "title", e.target.value)}
                                                  className="font-bold text-slate-900 w-full border-b border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none bg-transparent"
                                             />
                                             <input 
                                                  value={exp.duration}
                                                  onChange={(e) => updateExperience(i, "duration", e.target.value)}
                                                  className="text-xs text-slate-500 font-medium text-right w-32 border-b border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none bg-transparent"
                                             />
                                         </div>
                                         <button 
                                             onClick={() => removeExperience(i)}
                                             className="opacity-0 group-hover/entry:opacity-100 transition-opacity text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                                             title="Remove experience"
                                         >
                                             <X size={16} />
                                         </button>
                                     </div>
                                     <input 
                                          value={exp.company}
                                          onChange={(e) => updateExperience(i, "company", e.target.value)}
                                          className="text-sm text-slate-700 mb-2 w-full border-b border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none bg-transparent"
                                     />
                                     <div className="ml-4 space-y-1">
                                         {exp.bullets.map((b: string, j: number) => (
                                             <div key={j} className="flex gap-2 items-center text-sm text-slate-600">
                                                 <span className="h-1.5 w-1.5 rounded-full bg-slate-400 flex-shrink-0 mt-1.5" />
                                                 <AutoResizeTextarea 
                                                      value={b}
                                                      onChange={(e) => updateBullet(i, j, e.target.value)}
                                                      className="w-full p-1 -ml-1 rounded border border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none bg-transparent"
                                                      rows={1}
                                                 />
                                                 <button onClick={() => removeBullet(i, j)} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1">
                                                    <Trash size={16} />
                                                 </button>
                                             </div>
                                         ))}
                                     </div>

                                     <button onClick={() => addBullet(i)} className="mt-2 border p-2 rounded-sm text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                                        <Plus size={16} /> Add bullet point
                                    </button>
                                 </div>
                             ))}
                          </div>
                          </>
                          ) : (
                              <button 
                                  onClick={addExperience}
                                  className="mt-2 border border-dashed border-slate-300 p-3 rounded-md text-sm text-slate-500 hover:text-blue-600 hover:border-blue-400 flex items-center gap-2 w-full justify-center transition-colors"
                              >
                                  <Plus size={16} /> Add Experience
                              </button>
                          )}
                      </section>
                      )}
                      
                       {visibleSections.education && (
                       <section className="group/section relative">
                          <div className="flex items-center justify-between mb-3 border-b pb-1">
                              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">Education</h2>
                              <button 
                                  onClick={() => removeSection('education')}
                                  className="opacity-0 group-hover/section:opacity-100 transition-opacity text-red-500 hover:text-red-700 p-1"
                                  title="Remove section"
                              >
                                  <X size={16} />
                              </button>
                          </div>
                          {resumeData.education && resumeData.education.length > 0 ? (
                          <div className="space-y-4">
                             {resumeData.education.map((edu: any, i: number) => (
                                 <div key={i} className="group/entry relative border border-transparent hover:border-slate-200 rounded-lg p-2 -m-2 transition-colors">
                                     <div className="flex justify-between items-start gap-4">
                                         <div className="flex-1">
                                             <div className="flex justify-between items-baseline mb-1">
                                                 <input 
                                                     value={edu.institution}
                                                     onChange={(e) => updateEducation(i, "institution", e.target.value)}
                                                     className="font-bold text-slate-900 w-full border-b border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none bg-transparent"
                                                 />
                                                 <input 
                                                     value={edu.year}
                                                     onChange={(e) => updateEducation(i, "year", e.target.value)}
                                                     className="text-xs text-slate-500 font-medium text-right w-20 border-b border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none bg-transparent"
                                                 />
                                             </div>
                                             <input 
                                                 value={edu.degree}
                                                 onChange={(e) => updateEducation(i, "degree", e.target.value)}
                                                 className="text-sm text-slate-700 w-full border-b border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none bg-transparent"
                                             />
                                         </div>
                                         <button 
                                             onClick={() => removeEducation(i)}
                                             className="opacity-0 group-hover/entry:opacity-100 transition-opacity text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                                             title="Remove education"
                                         >
                                             <X size={16} />
                                         </button>
                                     </div>
                                 </div>
                             ))}
                          </div>
                          ) : (
                              <button 
                                  onClick={addEducation}
                                  className="mt-2 border border-dashed border-slate-300 p-3 rounded-md text-sm text-slate-500 hover:text-blue-600 hover:border-blue-400 flex items-center gap-2 w-full justify-center transition-colors"
                              >
                                  <Plus size={16} /> Add Education
                              </button>
                          )}
                      </section>
                      )}
                  </div>
              ) : activeTab === "cover_letter" ? (
                  <div className="max-w-2xl mx-auto">
                      <textarea
                          value={coverLetterText}
                          onChange={(e) => setCoverLetterText(e.target.value)}
                          className="w-full h-[600px] p-4 text-sm leading-relaxed font-serif rounded border border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none bg-transparent resize-none"
                      />
                  </div>
              ) : activeTab === "job_description" ? (
                  <div className="max-w-2xl mx-auto">
                      <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                          <h3 className="text-lg font-semibold mb-4 text-slate-900">Job Description</h3>
                          <div className="text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">
                              {jobDescription}
                          </div>
                      </div>
                  </div>
              ) : (
                  // Diff / Changes Tab (Not Editable)
                  <div className="max-w-3xl mx-auto space-y-8">
                     {/*data.diff?.summary && (
                         <section>
                             <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                 <History className="h-5 w-5 text-blue-500" />
                                 Summary Changes
                             </h3>
                             <div className="space-y-4">
                                {data.diff.summary.map((change: any, i: number) => (
                                    <div key={i} className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                                        <div className="flex gap-2 mb-2">
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded capitalize ${change.type === 'added' ? 'bg-green-100 text-green-700' : change.type === 'removed' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                                                {change.type}
                                            </span>
                                            <span className="text-xs text-slate-500">Why: {change.explanation}</span>
                                        </div>
                                        {change.original && (
                                            <div className="text-sm text-red-800 bg-red-50/50 p-2 rounded mb-2 decoration-red-900/20 line-through opacity-70">
                                                {change.original}
                                            </div>
                                        )}
                                        {change.new && (
                                            <div className="text-sm text-green-800 bg-green-50/50 p-2 rounded">
                                                {change.new}
                                            </div>
                                        )}
                                    </div>
                                ))}
                             </div>
                         </section>
                     )*/}
                     
                     {/*data.diff?.skills && (
                         <section>
                             <h3 className="text-lg font-semibold mb-4">Skills Gap Analysis</h3>
                             <div className="grid grid-cols-2 gap-4">
                                 <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                                     <h4 className="text-sm font-bold text-green-800 mb-2">Added Skills (Matched from JD)</h4>
                                     <div className="flex flex-wrap gap-2">
                                         {data.diff.skills.added && data.diff.skills.added.length > 0 ? (
                                             data.diff.skills.added.map((s: string, i: number) => (
                                                 <span key={i} className="text-xs bg-white border border-green-200 text-green-700 px-2 py-1 rounded shadow-sm">{s}</span>
                                             ))
                                         ) : <span className="text-xs text-slate-400">No new skills added</span>}
                                     </div>
                                 </div>
                                 <div className="bg-red-50 p-4 rounded-lg border border-red-100">
                                     <h4 className="text-sm font-bold text-red-800 mb-2">Removed / Low Relevance</h4>
                                      <div className="flex flex-wrap gap-2">
                                         {data.diff.skills.removed && data.diff.skills.removed.length > 0 ? (
                                             data.diff.skills.removed.map((s: string, i: number) => (
                                                 <span key={i} className="text-xs bg-white border border-red-200 text-red-700 px-2 py-1 rounded shadow-sm opacity-70 group-hover:opacity-100 transition-opacity">{s}</span>
                                             ))
                                         ) : <span className="text-xs text-slate-400">No skills removed</span>}
                                     </div>
                                 </div>
                             </div>
                         </section>
                     )*/}
                     
                     {/* <div className="text-center text-xs text-slate-400 pt-8 border-t">
                         Version ID: {data.version_id}
                     </div> */}
                  </div>
              )}
          </div>
      </div>
      
      {/* Feedback Modal */}
      <FeedbackModal 
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        documentId={data.document_id}
        onSubmit={() => {
            setFeedbackSubmitted(true);
            // Mark as submitted so we don't ask again for a long time
            localStorage.setItem('ra_feedback_submitted', Date.now().toString());
        }}
      />
    </div>
  );
}
